import type * as Types from '__generated__/zeus/types';
import { format } from 'date-fns';

/** Shaped rather than derived: the two callers read these fields off different generated queries. */
type MaybeCnmlTransaction = {
  __typename: string;
  selectedProduct?: Types.SellerProduct | null;
};

export const isCnmlClosedResale = (transaction: MaybeCnmlTransaction | null | undefined): boolean =>
  transaction?.__typename === 'ClosedTransaction' &&
  transaction.selectedProduct === 'SELL_WITH_UPSIDE';

export const isResaleListingUnderContract = (
  state: Types.HomeResaleListingState | null | undefined,
): boolean => state === 'IN_CONTRACT' || state === 'CONTINGENCIES_RELEASED';

export type ResalePhase = 'PreparingHomeResale' | 'ListedHomeResale' | 'SoldHomeResale';

/** In the URL rather than the phase name, so the query reads like the step it shows. */
export const RESALE_PHASE_SLUG: Record<ResalePhase, string> = {
  PreparingHomeResale: 'renovation',
  ListedHomeResale: 'on-the-market',
  SoldHomeResale: 'sold',
};

/**
 * A query rather than a path segment: the phase is not a separate resource, it is
 * the one state the home is in, so there is nothing at `/{phase}` to address. Being
 * a query also puts it on the observability context, via the allowlist in `_app`,
 * which is what lets RUM split per-view dwell time by phase.
 */
export const homeResaleDetailsPath = (addressId: string, phase?: ResalePhase): string =>
  phase
    ? `/dashboard/resale/${addressId}?phase=${RESALE_PHASE_SLUG[phase]}`
    : `/dashboard/resale/${addressId}`;

/** "Sold to Opendoor" has no `HomeResale` type: it is done before the resale starts. */
export const RESALE_STEPS: ReadonlyArray<{ label: string; phase: ResalePhase | null }> = [
  { label: 'Sold to Opendoor', phase: null },
  { label: 'Renovation', phase: 'PreparingHomeResale' },
  { label: 'On the market', phase: 'ListedHomeResale' },
  { label: 'Sold', phase: 'SoldHomeResale' },
];

export const resaleStepIndex = (phase: ResalePhase | null | undefined): number => {
  const found = RESALE_STEPS.findIndex((step) => step.phase === phase);
  // An unknown phase falls back to the acquisition step rather than blanking the tracker.
  return found === -1 ? 0 : found;
};

export const resalePhaseLabel = (phase: ResalePhase | null | undefined): string =>
  RESALE_STEPS[resaleStepIndex(phase)].label;

/**
 * `Date` scalars are zoneless calendar days, so they are rebuilt in local time
 * rather than parsed, which would shift them by the viewer's offset.
 */
export const formatResaleDay = (value: string | null | undefined): string | null => {
  const match = value ? /^(\d{4})-(\d{2})-(\d{2})/.exec(value) : null;
  if (!match) {
    return null;
  }
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const parsed = new Date(year, month - 1, day);
  // `Date` rolls impossible days forward: 2026-02-30 would format as March 2.
  return parsed.getMonth() === month - 1 && parsed.getDate() === day
    ? format(parsed, 'MMM do')
    : null;
};
